import {
  Analytics,
  Error500Fragment,
  PaginatedModel,
  SvgSprite,
} from "@skv/core";
import { useViewModel } from "@skv/state";
import * as React from "react";
import { Link } from "react-router-dom";

import { SuggestedOpportunity } from "~/ui/SuggestedOpportunity";

import * as qs from "qs";

import classNames from "classnames";

import { pluraliser } from "@skv/util";

interface BasicPaginationProps {
  currentPage: number;
  totalPages: number;
}

const BasicPagination: React.FC<BasicPaginationProps> = ({
  currentPage,
  totalPages,
}) => {
  return (
    <nav
      className="py-3 mx-n3"
      css={`
        display: flex;
        justify-content: space-between;
      `}
    >
      <Link
        className={classNames("px-3", {
          invisible: currentPage === 1,
        })}
        to={(loc) => {
          const query = qs.parse(loc.search, { ignoreQueryPrefix: true });
          query.page = `${currentPage - 1}`;

          return {
            ...loc,
            search: qs.stringify(query),
            state: loc.state,
          };
        }}
      >
        <SvgSprite id="icons:chev-left" width={12} height={12} />
        Previous
      </Link>

      <span className="px-3 mx-auto">
        Page {currentPage} of {totalPages}
      </span>
      <Link
        className={classNames("px-3", {
          invisible: currentPage === totalPages,
        })}
        to={(loc) => {
          const query = qs.parse(loc.search, { ignoreQueryPrefix: true });
          query.page = `${currentPage + 1}`;

          return {
            ...loc,
            search: qs.stringify(query),
            state: loc.state,
          };
        }}
      >
        Next
        <SvgSprite id="icons:chev-right" width={12} height={12} />
      </Link>
    </nav>
  );
};

type SuggestionsViewModel = PaginatedModel<
  {
    id: number;
    name: string;
    organisationName: string;
    url: string;
  },
  "suggestions"
>;

export const Suggestions = () => {
  const [{ data, error, status }] = useViewModel<SuggestionsViewModel>();

  if (status === "loading") {
    return <p className="mt-3">Loading, please wait...</p>;
  }

  if (status === "error") {
    return <Error500Fragment />;
  }

  return (
    <>
      <header>
        <h2
          className="display-3 font-weight-bold border-bottom mt-n2 mb-0"
          css={`
            padding-bottom: 1.25rem;
          `}
        >
          {pluraliser(data.totalCount, {
            none: "You have no suggestions",
            plural: `You have ${data.totalCount} suggestions`,
            singular: "You have 1 suggestion",
          })}
        </h2>
      </header>

      {data.totalCount !== 0 && (
        <>
          <ul className="list-unstyled mb-0">
            {data.suggestions.map((n) => (
              <li key={n.id}>
                <SuggestedOpportunity
                  to={(loc) => ({
                    pathname: n.url,
                    state: {
                      referrer: {
                        ...loc,
                        state: {
                          children: "Suggestions",
                        },
                      },
                    },
                  })}
                  title={n.name}
                  organisation={n.organisationName}
                />
              </li>
            ))}
          </ul>
          <BasicPagination
            currentPage={data.page}
            totalPages={data.totalPages}
          />

          <Analytics
            data={{
              subSection: "volunteers",
              subSubSection: "activity",
              subSubSubSection: "suggestions",
              pageTitle: "suggestions",
              loginStatus: "loggedin",
            }}
          />
        </>
      )}
    </>
  );
};
